//@ts-ignore
import io, { Socket } from 'socket.io-client';
import { fork, take, call, cancel, takeLatest, put } from 'redux-saga/effects';
import {
    setOnlineStatus,
    disconnect,
    disconnectSuccess,
    setOnlineUsers,
    setOfflineUsers,
    NewContentType,
} from './action';
import { retrieveJwt } from '../authentication';
import { types } from './types';
import { SOCKET } from '../../configurations/api/url';
import { SOCKET_REQUESTS } from './messages';
import { getJWtDetails } from '../../utils/AppUtils';
import { annouceNewFollower } from '../audience/action';
import { newContent, setNewContentAvailable } from '../messages/actions';
import {
    campaignWalletDepositCompleted,
    workspaceSubmissionRevisionReceived,
    workspaceSubmissionApprovedReceived,
    workspaceLiveUrlRevisionReceived,
} from '../buzzbridge/actions';
import type { ICampaignWalletDepositSocketPayload } from '../buzzbridge/types';
import { eventChannel } from 'redux-saga';
import { recieveNotification } from '../notifications/action';
import { refreshUserDashboard } from '../account/actions';
import { nuke } from '../cache/action';
import { WebNotification } from '../account/accountDetailsResponse.type';
import { Follower } from '../user/model';
import { IProject } from '../../types/global/helper';
import { mediaForgeWorker, updateProjectSuccess } from '../project/actions';
import { genericParseSingleDocument } from '../../utils/responseProcessor';
import { logout } from '../authentication/action';

function connect(jwt: any, userID: string) {
    const socket = io(SOCKET, {
        forceNew: true,
        transports: ['websocket', 'polling'],
        secure: true,
        transportOptions: {
            polling: {
                extraHeaders: {
                    Authorization: `Bearer ${jwt}`,
                    'X-User': userID,
                },
            },
            websocket: {
                extraHeaders: {
                    Authorization: `Bearer ${jwt}`,
                    'X-User': userID,
                },
            },
        },
        withCredentials: true,
        auth: {
            token: `Bearer ${jwt}`,
            userID,
        },
    });
    socket.emit(SOCKET_REQUESTS.ONLINE, { status: 'online' });
    return new Promise(resolve => {
        socket.on('connect', () => {
            resolve(socket);
        });
        socket.on('connect_error', () => {
            socket.io.opts.transports = ['websocket'];
        });
    });
}

function subscribe(socket: Socket) {
    return eventChannel(emit => {
        socket.on(SOCKET_REQUESTS.CLEAR_CACHE, ({ message }: { message: string }) => {
            /** The server dispatches request to clear cache every 12 hours.
             * This listens for that. Trigger the nuke cache here to clear all users cache
             */
            emit(nuke());
        });

        socket.on(
            SOCKET_REQUESTS.NEW_FOLLOWER,
            ({ details }: { follower: Follower; type: 'user' | 'project'; details: WebNotification }) => {
                emit(recieveNotification(details));
                /** New Follower. Refresh User dashboard to show nfollowing number */
                emit(refreshUserDashboard());
            },
        );

        /** Preparing this for User Feed */
        socket.on(SOCKET_REQUESTS.NEW_ACTIVITY, ({ type, payload }: { type: 'user' | 'project'; payload: any }) => {
            /** New Follower. Refresh User dashboard to show nfollowing number */
        });

        socket.on(SOCKET_REQUESTS.DISCONNECT, e => {
            // TODO: handle
            emit(disconnectSuccess({ connected: false }));
        });

        socket.on(SOCKET_REQUESTS.SET_ONLINE_USERS, ({ user }) => {
            emit(setOnlineUsers(user));
        });

        socket.on(SOCKET_REQUESTS.SET_OFFLINE_USERS, ({ user }) => {
            emit(setOfflineUsers(user));
        });

        socket.on(SOCKET_REQUESTS.NEW_CONTENT_AVAILABLE, ({ content_type }: { content_type: NewContentType }) => {
            emit(setNewContentAvailable(content_type));
        });

        socket.on(
            SOCKET_REQUESTS.MEDIA_PROCESSING_COMPLETE,
            ({
                project,
                project_id,
                details,
                type,
            }: {
                project: IProject;
                project_id: string;
                details: WebNotification;
                type: 'thumbnail' | 'quality';
            }) => {
                /** The server dispatches request to clear cache every 12 hours.
                 * This listens for that. Trigger the nuke cache here to clear all users cache
                 */
                if (type === 'thumbnail') {
                    emit(
                        mediaForgeWorker({
                            status: 'awaiting processing',
                            mediaForgeMessage: 'Thumbnail Processing Completed...🥳',
                            project: project,
                        }),
                    );
                }

                if (type === 'quality') {
                    emit(
                        mediaForgeWorker({
                            status: 'completed',
                            mediaForgeMessage: 'Project Processing Completed...🥳',
                            project: project,
                        }),
                    );
                    const parsedProject = genericParseSingleDocument(project);
                    emit(updateProjectSuccess(parsedProject));
                    emit(recieveNotification(details));
                }
            },
        );

        socket.on(SOCKET_REQUESTS.LOGOUT, ({ message, userId }) => {
            emit(logout(true));
        });

        socket.on(
            SOCKET_REQUESTS.CONTENT_REVISION_REQUESTED,
            ({
                submissionId,
                deliverableType,
                feedback,
            }: {
                submissionId: string;
                deliverableType: string;
                feedback: string;
            }) => {
                emit(workspaceSubmissionRevisionReceived({ submissionId, deliverableType, feedback }));
            },
        );

        socket.on(
            SOCKET_REQUESTS.CONTENT_APPROVED,
            ({ submissionId, deliverableType }: { submissionId: string; deliverableType: string }) => {
                emit(workspaceSubmissionApprovedReceived({ submissionId, deliverableType }));
            },
        );

        socket.on(
            SOCKET_REQUESTS.LIVE_URL_REVISION_REQUESTED,
            ({
                submissionId,
                deliverableType,
                feedback,
            }: {
                submissionId: string;
                deliverableType: string;
                feedback: string;
            }) => {
                emit(workspaceLiveUrlRevisionReceived({ submissionId, deliverableType, feedback }));
            },
        );

        socket.on(SOCKET_REQUESTS.CAMPAIGN_WALLET_DEPOSIT_COMPLETED, (payload: ICampaignWalletDepositSocketPayload) => {
            emit(campaignWalletDepositCompleted(payload));
        });

        return () => {};
    });
}

function* read(socket: Socket): any {
    const channel = yield call(subscribe, socket);
    while (true) {
        const action = yield take(channel);
        yield put(action);
    }
}

function* followUser(socket: Socket) {
    while (true) {
        const { payload } = yield take(`${annouceNewFollower}`);
        socket.emit(SOCKET_REQUESTS.FOLLOW_THSPIAN, payload);
    }
}

/** Set whether a user is online or offline */
function* setUserOnlineStatus(socket: Socket) {
    while (true) {
        const { payload } = yield take(`${setOnlineStatus}`);
        socket.emit(SOCKET_REQUESTS.ONLINE, { status: payload });
    }
}

function* newContentDispatcher(socket: Socket) {
    while (true) {
        const { payload } = yield take(`${newContent}`);
        socket.emit(SOCKET_REQUESTS.NEW_CONTENT, { content_type: payload });
    }
}

function* handleIO(socket: Socket) {
    yield fork(newContentDispatcher, socket);
    yield fork(setUserOnlineStatus, socket);
    yield fork(read, socket);
    yield fork(followUser, socket);
}

function* flow(): any {
    while (true) {
        const jwt = yield retrieveJwt();
        const { isSignedIn, user_id } = getJWtDetails(jwt);
        if (isSignedIn) {
            const socket = yield call(connect, jwt, user_id);
            const task = yield fork(handleIO, socket);
            yield take(`${disconnect}`);
            yield cancel(task);
            socket.emit(SOCKET_REQUESTS.DISCONNECT);
        }
    }
}

function* flowWatcher() {
    yield takeLatest(types.CONNECT, flow);
}

export default function* socketSaga() {
    yield fork(flowWatcher);
}
